import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Button, Typography, Card, message, Spin } from 'antd';
import { ArrowLeftOutlined, SettingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import api, { monthlyMetricService } from '../../services/api';

// Bileşenler
import ControlPanel from '../AllBrandsMetrics/components/ControlPanel';
import MetricSelector from '../AllBrandsMetrics/components/MetricSelector';
import SummaryTable from '../AllBrandsMetrics/components/SummaryTable';

// Hook'lar
// import useBrandsData from '../AllBrandsMetrics/hooks/useBrandsData'; // Kullanmıyoruz
import useMetricsData from '../AllBrandsMetrics/hooks/useMetricsData';

const { Title, Text } = Typography;

dayjs.locale('tr');

const MetricsSummary = () => {
  const navigate = useNavigate();
  const [isMetricSelectorVisible, setIsMetricSelectorVisible] = useState(false);
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [selectedMetricDescriptions, setSelectedMetricDescriptions] = useState([]);
  const [showSummaryTable, setShowSummaryTable] = useState(false);
  const [monthlyMetricsData, setMonthlyMetricsData] = useState({});
  
  // Sadece gerekli hook'ları kullan
  const {
    selectedMonth,
    isPickerOpen,
    setIsPickerOpen,
    allMetricsData,
    handleMonthChange,
    handleRecalculateAllBrands
  } = useMetricsData();
  
  // Manual brands state (hook kullanmıyoruz çünkü selectedMonth dependency'sini istemiyoruz)
  const [allBrands, setAllBrands] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [isFiltering, setIsFiltering] = useState(false);
  const searchInputRef = useRef(null);
  const [advisors] = useState([]);
  const [advisorsLoading] = useState(false);
  const [selectedAdvisor, setSelectedAdvisor] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  
  // Advisor change handler
  const handleAdvisorChange = useCallback((value) => {
    setSelectedAdvisor(value);
  }, []);
  
  // Status change handler
  const handleStatusChange = useCallback((value) => {
    setSelectedStatus(value);
  }, []);
  
  // Tüm aktif markaların aylık metriklerini çek
  const fetchAllActiveBrandsMonthlyMetrics = useCallback(async (date, skipLoading = false) => {
    try {
      if (!skipLoading) setLoading(true);
      const formattedDate = date ? dayjs(date).format('YYYY-MM') : dayjs().format('YYYY-MM');
      const data = await monthlyMetricService.getAllActiveBrandsMonthlyMetrics(formattedDate);
      setMonthlyMetricsData(data);
    } catch (error) {
      console.error('Aktif markaların aylık metrik verileri çekilirken hata:', error);
      message.error('Aylık metrik verileri yüklenirken hata oluştu');
    } finally {
      if (!skipLoading) setLoading(false);
    }
  }, [setLoading]);

  // Initialization flag to prevent multiple initializations
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Component mount edildiğinde tek seferde tüm verileri yükle
  useEffect(() => {
    if (isInitialized) return;
    
    const initializeData = async () => {
      try {
        setLoading(true);
        
        // Tüm markaları manuel olarak yükle (hook'a selectedMonth vermediğimiz için)
        const brandsResponse = await api.get('/brands', {
          params: {
            limit: 1000,
            offset: 0,
            orderBy: 'displayOrder',
            orderDirection: 'asc',
            includeInactive: 'true',
            date: selectedMonth ? selectedMonth.format('YYYY-MM') : null
          }
        });
        
        const brandsData = brandsResponse.data.sort((a, b) => {
          const orderA = a.displayOrder || 0;
          const orderB = b.displayOrder || 0;
          return orderA - orderB;
        });
        
        // Brands state'ini manuel güncelle - Tüm markaları göster
        setAllBrands(brandsData);
        setBrands(brandsData); // Tüm markaları göster, 100 sınırını kaldır
        
        // Kampanyaları yükle
        try {
          const monthStart = selectedMonth.startOf('month').format('YYYY-MM-DD');
          const monthEnd = selectedMonth.endOf('month').format('YYYY-MM-DD');
          
          await api.get('/campaigns', {
            params: {
              startDate: monthStart,
              endDate: monthEnd
            }
          });
        } catch (error) {
          console.error('Kampanyalar yüklenirken hata:', error);
        }
        
        // Aylık metrik verilerini yükle
        if (selectedMonth) {
          await fetchAllActiveBrandsMonthlyMetrics(selectedMonth, true);
        }
        
        // Zorunlu varsayılan metrikler - her zaman gösterilecek
        const mandatoryMetricDescriptions = [
          'TOPLAM CİRO',
          'CİRO HEDEF SAPMASI', 
          'AY SONU TAHMİNİ CİRO',
          'ÖNCEKİ AYA GÖRE TAHMİNİ DEĞİŞİM',
          'GÜNLÜK ORTALAMA CİRO',
          'GÜNLÜK GEREKEN CİRO',
          'ROI',
          'HEDEF ROI'
        ];
        
        // localStorage'dan ek metrik seçimlerini yükle ve zorunlu metriklerle birleştir
        try {
          const savedMetricDescriptions = localStorage.getItem('selectedMetricDescriptions');
          const savedMetrics = localStorage.getItem('selectedMetrics');
          
          let finalMetricDescriptions = [...mandatoryMetricDescriptions];
          let finalMetrics = [];
          
          if (savedMetricDescriptions) {
            try {
              const descriptionsArray = JSON.parse(savedMetricDescriptions);
              if (Array.isArray(descriptionsArray)) {
                // Zorunlu metrikler + kaydedilmiş ek metrikler
                const additionalMetrics = descriptionsArray.filter(desc => 
                  !mandatoryMetricDescriptions.includes(desc)
                );
                finalMetricDescriptions = [...mandatoryMetricDescriptions, ...additionalMetrics];
                
                if (savedMetrics) {
                  const metricsArray = JSON.parse(savedMetrics);
                  if (Array.isArray(metricsArray)) {
                    finalMetrics = metricsArray;
                  }
                }
              }
            } catch (e) {
              console.error('Description parsing hatası:', e);
            }
          } else if (savedMetrics) {
            try {
              const metricsArray = JSON.parse(savedMetrics);
              if (Array.isArray(metricsArray)) {
                finalMetrics = metricsArray;
              }
            } catch (e) {
              console.error('Metrics parsing hatası:', e);
            }
          }
          
          setSelectedMetricDescriptions(finalMetricDescriptions);
          if (finalMetrics.length > 0) {
            setSelectedMetrics(finalMetrics);
          }
          // Zorunlu metrikler olduğu için her zaman tablo göster
          setShowSummaryTable(true);
          
        } catch (error) {
          console.error('localStorage erişim hatası:', error);
          // localStorage erişim hatası varsa sadece zorunlu metrikleri kullan
          setSelectedMetricDescriptions(mandatoryMetricDescriptions);
          // Zorunlu metrikler olduğu için her zaman tablo göster
          setShowSummaryTable(true);
        }
        
        setIsInitialized(true);
      } catch (error) {
        console.error('Veri yükleme hatası:', error);
        message.error('Veriler yüklenirken hata oluştu');
      } finally {
        setLoading(false);
      }
    };
    
    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Sadece mount'da çalış - dependencies'i ignore et
  
  // Ay değiştiğinde aylık metrik verilerini yeniden çek
  useEffect(() => {
    if (!isInitialized || !selectedMonth) return;
    
    fetchAllActiveBrandsMonthlyMetrics(selectedMonth);
  }, [selectedMonth, fetchAllActiveBrandsMonthlyMetrics, isInitialized]);
  
  // ControlPanel için wrapper fonksiyonlar
  const handlePickerOpenChange = useCallback((open) => {
    if (typeof setIsPickerOpen === 'function' && open !== isPickerOpen) {
      setIsPickerOpen(open);
    }
  }, [setIsPickerOpen, isPickerOpen]);
  
  const handleMonthChangeWrapper = useCallback((date) => {
    // Ay değiştiğinde sadece selectedMonth'u güncelle
    // useEffect zaten ay değişimini yakalayacak ve gerekli API çağırılarını yapacak
    handleMonthChange(date, brands, setLoading);
    
    // Seçili metrikleri koru
    if (selectedMetrics.length > 0) {
      try {
        localStorage.setItem('selectedMetrics', JSON.stringify(selectedMetrics));
        localStorage.setItem('selectedMetricDescriptions', JSON.stringify(selectedMetricDescriptions));
      } catch (error) {
        console.error('localStorage kaydetme hatası:', error);
      }
    }
  }, [handleMonthChange, brands, setLoading, selectedMetrics, selectedMetricDescriptions]);
  
  const handleRecalculateAllBrandsWrapper = useCallback(() => {
    handleRecalculateAllBrands(brands, setLoading);
  }, [handleRecalculateAllBrands, brands, setLoading]);
  
  // Geri dön
  const handleGoBack = () => {
    navigate('/all-brands-metrics');
  };
  
  // Metrik seçici popup'ı göster/gizle
  const toggleMetricSelector = () => {
    setIsMetricSelectorVisible(prev => !prev);
  };
  
  // Seçilen metrikleri kaydet
  const handleMetricsSave = (metrics, descriptions) => {
    const metricIds = Array.isArray(metrics) ? metrics : [];
    const metricDescriptions = Array.isArray(descriptions) ? descriptions : [];
    
    setSelectedMetrics(metricIds);
    setSelectedMetricDescriptions(metricDescriptions);
    
    try {
      if (metricIds.length > 0) {
        localStorage.setItem('selectedMetrics', JSON.stringify(metricIds));
        localStorage.setItem('selectedMetricDescriptions', JSON.stringify(metricDescriptions));
      } else {
        localStorage.removeItem('selectedMetrics');
        localStorage.removeItem('selectedMetricDescriptions');
      }
    } catch (error) {
      console.error('localStorage kaydetme hatası:', error);
    }
    
    setIsMetricSelectorVisible(false);
    
    if (metricIds.length > 0) {
      setShowSummaryTable(true);
    } else {
      setShowSummaryTable(false);
    }
  };
  
  // Özet tabloyu kapat
  const handleCloseSummaryTable = () => {
    setSelectedMetrics([]);
    setSelectedMetricDescriptions([]);
    setShowSummaryTable(false);
    
    try {
      localStorage.removeItem('selectedMetrics');
      localStorage.removeItem('selectedMetricDescriptions');
    } catch (error) {
      console.error('localStorage temizleme hatası:', error);
    }
  };
  
  // Arama değeri, advisor filtresi ve status değiştiğinde markaları filtrele
  useEffect(() => {
    const searchFilterActive = searchValue && searchValue.trim() !== '';
    const advisorFilterActive = selectedAdvisor !== null;
    const statusFilterActive = selectedStatus !== null;
    
    if (!searchFilterActive && !advisorFilterActive && !statusFilterActive) {
      setIsFiltering(false);
      setFilteredBrands([]);
      return;
    }
    
    setIsFiltering(true);
    
    let filtered = [...allBrands];
    
    if (searchFilterActive) {
      filtered = filtered.filter(brand => 
        brand.name.toLowerCase().includes(searchValue.toLowerCase())
      );
    }
    
    if (advisorFilterActive) {
      filtered = filtered.filter(brand => 
        brand.advisor === selectedAdvisor
      );
    }
    
    if (statusFilterActive) {
      filtered = filtered.filter(brand => 
        brand.status === selectedStatus
      );
    }
    
    filtered = filtered.sort((a, b) => {
      const orderA = a.displayOrder || 0;
      const orderB = b.displayOrder || 0;
      return orderA - orderB;
    });
    
    setFilteredBrands(filtered);
  }, [searchValue, selectedAdvisor, selectedStatus, allBrands]);
  
  // Görünür markalar
  const visibleBrands = useMemo(() => {
    return isFiltering ? filteredBrands : brands;
  }, [isFiltering, filteredBrands, brands]);
  
  return (
    <div className="px-2 sm:px-4 lg:px-6 py-2 sm:py-4">
      <div className="space-y-3 sm:space-y-4 lg:space-y-6">
        {/* Başlık ve geri dön butonu */}
        <Card>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={handleGoBack}
                size="large"
              >
                Geri
              </Button>
              <div>
                <Title level={2} style={{ margin: 0 }}>
                  Marka Metrikleri Özeti
                </Title>
                <Text type="secondary">
                  Tüm markaların seçilen metriklerini karşılaştırın
                </Text>
              </div>
            </div>
            <Button
              type="primary"
              icon={<SettingOutlined />}
              onClick={toggleMetricSelector}
              size="large"
            >
              Metrik Seç
            </Button>
          </div>
        </Card>
        
        {/* Kontrol paneli */}
        <ControlPanel
          selectedMonth={selectedMonth}
          searchValue={searchValue}
          loading={loading}
          searchInputRef={searchInputRef}
          handleMonthChange={handleMonthChangeWrapper}
          handleRecalculateAllBrands={handleRecalculateAllBrandsWrapper}
          setSearchValue={setSearchValue}
          setIsPickerOpen={handlePickerOpenChange}
          isPickerOpen={isPickerOpen}
          isEmbedded={false}
          isAllBrandsToken={false}
          advisors={advisors}
          advisorsLoading={advisorsLoading}
          selectedAdvisor={selectedAdvisor}
          handleAdvisorChange={handleAdvisorChange}
          onMetricSelectorClick={toggleMetricSelector}
          selectedStatus={selectedStatus}
          handleStatusChange={handleStatusChange}
          hideMetricSelector={true} // Metrik seçici butonunu gizle
        />
        
        {/* Metrik Seçici Popup */}
        <MetricSelector
          open={isMetricSelectorVisible}
          onCancel={() => setIsMetricSelectorVisible(false)}
          onSave={handleMetricsSave}
          allMetricsData={allMetricsData}
          monthlyMetricsData={monthlyMetricsData}
          brands={visibleBrands}
          selectedMetrics={selectedMetrics}
        />
        
        {/* Loading durumu */}
        {loading && brands.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" tip="Markalar yükleniyor..." />
          </div>
        )}
        
        {/* Özet Tablo */}
        {showSummaryTable && brands.length > 0 ? (
          <SummaryTable
            brands={visibleBrands}
            selectedMetrics={selectedMetrics}
            selectedMetricDescriptions={selectedMetricDescriptions}
            allMetricsData={allMetricsData}
            monthlyMetricsData={monthlyMetricsData}
            selectedMonth={selectedMonth}
            onClose={handleCloseSummaryTable}
            mandatoryMetricDescriptions={[
              'TOPLAM CİRO',
              'CİRO HEDEF SAPMASI', 
              'AY SONU TAHMİNİ CİRO',
              'ÖNCEKİ AYA GÖRE TAHMİNİ DEĞİŞİM',
              'GÜNLÜK ORTALAMA CİRO',
              'GÜNLÜK GEREKEN CİRO',
              'ROI',
              'HEDEF ROI'
            ]}
          />
        ) : !loading && brands.length > 0 && (
          <Card>
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Title level={4} type="secondary">
                Metrik Seçimi Yapılmamış
              </Title>
              <Text type="secondary">
                Özet tablosunu görüntülemek için lütfen metrikleri seçin.
              </Text>
              <br />
              <Button
                type="primary"
                onClick={toggleMetricSelector}
                style={{ marginTop: 16 }}
              >
                Metrik Seç
              </Button>
            </div>
          </Card>
        )}
        
        {/* Markalar yüklenmemişse */}
        {!loading && brands.length === 0 && (
          <Card>
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Title level={4} type="secondary">
                Marka Bulunamadı
              </Title>
              <Text type="secondary">
                Seçilen ay için marka verisi bulunamadı.
              </Text>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MetricsSummary;